import type { LookupValue, Team } from "@/types/task";
import type { UserProfile } from "@/types/user";
import * as Select from "@radix-ui/react-select";
import * as Label from "@radix-ui/react-label";
import {
  TbChevronDown,
  TbLoader2,
  TbPlus,
  TbCheck,
  TbCalendar,
  TbSearch,
} from "react-icons/tb";
import { useState } from "react";
import { useTemplateStore } from "@/stores/templateStore";
import { useModalStore } from "@/stores/modalStore";
import { TemplateStepManager } from "./TemplateStepManager";
import { useQuery } from "@tanstack/react-query";
import { LayerService } from "@/services/layerService";

interface TemplateFormProps {
  categories: LookupValue[];
  statuses: LookupValue[];
  priorities: LookupValue[];
  users: UserProfile[];
  teams: Team[];
  onSubmit: () => void;
}

export const TemplateForm: React.FC<TemplateFormProps> = ({
  categories,
  statuses,
  priorities,
  users,
  teams,
  onSubmit,
}) => {
  // Get template data and actions from store
  const {
    newTemplateData,
    templateSteps,
    setNewTemplateData,
    setTemplateSteps,
    validationError,
  } = useTemplateStore();

  // Modal state from modalStore
  const {
    editingTemplate,
    showTemplateSteps,
    openTemplateSteps,
    closeTemplateSteps,
  } = useModalStore();

  const [errors, setErrors] = useState<{ start?: string; due?: string }>({});
  const [isSaving, setIsSaving] = useState(false);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState("");

  // Fetch ArcGIS layers from database
  const { data: arcgisLayers = [] } = useQuery({
    queryKey: ["asset-layers"],
    queryFn: LayerService.getAllLayers,
  });

  const isEditing = !!editingTemplate;

  // Helper functions for search functionality
  const filterUsers = (searchTerm: string): UserProfile[] => {
    if (!searchTerm.trim()) return users;

    const term = searchTerm.toLowerCase();
    return users.filter((user) => {
      const fullName = `${user.first_name || ""} ${user.last_name || ""}`
        .trim()
        .toLowerCase();
      const email = user.email.toLowerCase();
      return fullName.includes(term) || email.includes(term);
    });
  };

  const filterTeams = (searchTerm: string): Team[] => {
    if (!searchTerm.trim()) return teams;

    const term = searchTerm.toLowerCase();
    return teams.filter((team) => team.name.toLowerCase().includes(term));
  };

  // Validation for days
  const validateDays = (start?: number, due?: number) => {
    if (typeof start === "number" && typeof due === "number" && start > due) {
      setErrors({
        start: "Start days cannot be greater than due days",
        due: "Due days cannot be less than start days",
      });
      return false;
    }
    setErrors({});
    return true;
  };

  return showTemplateSteps ? (
    <div>
      {/* Step Manager */}
      <TemplateStepManager
        templateId={
          isEditing && editingTemplate?.id
            ? editingTemplate.id
            : `temp_${Date.now()}`
        }
        steps={templateSteps}
        onStepsChange={setTemplateSteps}
        users={users}
        teams={teams}
      />

      {/* Save Steps Button */}
      <button
        onClick={() => closeTemplateSteps()}
        className="bg-[#374795] text-white rounded-[10px] px-6 py-3 w-full text-[14px] font-semibold mt-0.5 hover:bg-[#132477] cursor-pointer"
      >
        Save Steps
      </button>
    </div>
  ) : (
    <div>
      <div className="flex flex-col items-start w-full overflow-y-auto min-h-[60vh] max-h-[60vh] p-6 rounded-[10px] border border-gray-100">
        {/* Show validation error if any */}
        {validationError && (
          <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded mb-4">
            {validationError}
          </div>
        )}

        <div className="space-y-4 w-full">
          {/* Template Name */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <Label.Root className="text-[#444444] text-[14px] font-semibold">
                Template Name <span className="text-[#e7000b]">*</span>
              </Label.Root>
            </div>
            <div className="bg-gray-100 rounded-[10px] px-3.5 py-[13px]">
              <input
                type="text"
                value={newTemplateData.name}
                onChange={(e) => setNewTemplateData({ name: e.target.value })}
                className="w-full bg-transparent text-[#828282] text-[12px] placeholder:text-[#828282] border-none outline-none"
                placeholder="Type Here"
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label.Root className="text-[#444444] text-[14px] font-semibold">
              Descriptions
            </Label.Root>
            <div className="bg-gray-100 rounded-[10px] px-3.5 pt-[13px] pb-10">
              <textarea
                value={newTemplateData.description}
                onChange={(e) =>
                  setNewTemplateData({ description: e.target.value })
                }
                className="w-full h-full bg-transparent text-[#828282] text-[12px] placeholder:text-[#828282] border-none outline-none resize-none"
                placeholder="Type Here"
                rows={1}
              />
            </div>
          </div>

          {/* Layer Name & Category */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Label.Root className="text-[#444444] text-[14px] font-semibold">
                  layer Name <span className="text-[#e7000b]">*</span>
                </Label.Root>
              </div>
              <Select.Root
                value={newTemplateData.layer_name}
                onValueChange={(value) =>
                  setNewTemplateData({ layer_name: value })
                }
              >
                <Select.Trigger className="w-full bg-gray-100 rounded-[10px] px-3.5 py-[13px] flex items-center justify-between text-[#828282] text-[12px]">
                  <Select.Value placeholder="Select" />
                  <Select.Icon>
                    <TbChevronDown
                      size={16}
                      className="text-[#828282] rotate-[270deg]"
                    />
                  </Select.Icon>
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content className="z-[200000] bg-white rounded-xl shadow-[0px_0px_24px_0px_rgba(0,0,0,0.08)] border-0 max-h-60 overflow-auto p-1">
                    <Select.Viewport>
                      <Select.Item
                        key="no_layer"
                        value="no_layer"
                        className="relative cursor-pointer select-none py-2.5 px-3 mx-1 rounded-lg hover:bg-gray-50 focus:bg-gray-50 data-[state=checked]:bg-blue-50 data-[state=checked]:text-blue-700 text-[#444444] text-[12px] font-medium transition-colors outline-none"
                      >
                        <Select.ItemText>
                          No Layer (General Template)
                        </Select.ItemText>
                      </Select.Item>
                      {arcgisLayers
                        .filter((layer) => layer.key !== "at_boundaries")
                        .map((layer) => (
                          <Select.Item
                            key={layer.key}
                            value={layer.key}
                            className="relative cursor-pointer select-none py-2.5 px-3 mx-1 rounded-lg hover:bg-gray-50 focus:bg-gray-50 data-[state=checked]:bg-blue-50 data-[state=checked]:text-blue-700 text-[#444444] text-[12px] font-medium transition-colors outline-none"
                          >
                            <Select.ItemText>{layer.label}</Select.ItemText>
                          </Select.Item>
                        ))}
                    </Select.Viewport>
                  </Select.Content>
                </Select.Portal>
              </Select.Root>
            </div>

            <div className="space-y-1.5">
              <Label.Root className="text-[#444444] text-[14px] font-semibold">
                Category
              </Label.Root>
              <Select.Root
                value={newTemplateData.category_id}
                onValueChange={(value) =>
                  setNewTemplateData({ category_id: value })
                }
              >
                <Select.Trigger className="w-full bg-gray-100 rounded-[10px] px-3.5 py-[13px] flex items-center justify-between text-[#828282] text-[12px]">
                  <Select.Value placeholder="Select" />
                  <Select.Icon>
                    <TbChevronDown
                      size={16}
                      className="text-[#828282] rotate-[270deg]"
                    />
                  </Select.Icon>
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content className="z-[200000] bg-white rounded-xl shadow-[0px_0px_24px_0px_rgba(0,0,0,0.08)] border-0 max-h-60 overflow-auto p-1">
                    <Select.Viewport>
                      {categories.map((category) => (
                        <Select.Item
                          key={category.id}
                          value={category.id}
                          className="relative cursor-pointer select-none py-2.5 px-3 mx-1 rounded-lg hover:bg-gray-50 focus:bg-gray-50 data-[state=checked]:bg-blue-50 data-[state=checked]:text-blue-700 text-[#444444] text-[12px] font-medium transition-colors outline-none"
                        >
                          <Select.ItemText>{category.label}</Select.ItemText>
                        </Select.Item>
                      ))}
                    </Select.Viewport>
                  </Select.Content>
                </Select.Portal>
              </Select.Root>
            </div>
          </div>

          {/* Priority & Default Status */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label.Root className="text-[#444444] text-[14px] font-semibold">
                Priority
              </Label.Root>
              <Select.Root
                value={newTemplateData.priority_id}
                onValueChange={(value) =>
                  setNewTemplateData({ priority_id: value })
                }
              >
                <Select.Trigger className="w-full bg-gray-100 rounded-[10px] px-3.5 py-[13px] flex items-center justify-between text-[#828282] text-[12px]">
                  <Select.Value placeholder="Select" />
                  <Select.Icon>
                    <TbChevronDown
                      size={16}
                      className="text-[#828282] rotate-[270deg]"
                    />
                  </Select.Icon>
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content className="z-[200000] bg-white rounded-xl shadow-[0px_0px_24px_0px_rgba(0,0,0,0.08)] border-0 max-h-60 overflow-auto p-1">
                    <Select.Viewport>
                      {priorities.map((priority) => (
                        <Select.Item
                          key={priority.id}
                          value={priority.id}
                          className="relative cursor-pointer select-none py-2.5 px-3 mx-1 rounded-lg hover:bg-gray-50 focus:bg-gray-50 data-[state=checked]:bg-blue-50 data-[state=checked]:text-blue-700 text-[#444444] text-[12px] font-medium transition-colors outline-none"
                        >
                          <Select.ItemText>{priority.label}</Select.ItemText>
                        </Select.Item>
                      ))}
                    </Select.Viewport>
                  </Select.Content>
                </Select.Portal>
              </Select.Root>
            </div>

            <div className="space-y-1.5">
              <Label.Root className="text-[#444444] text-[14px] font-semibold">
                Default Status
              </Label.Root>
              <Select.Root
                value={newTemplateData.status_id}
                onValueChange={(value) =>
                  setNewTemplateData({ status_id: value })
                }
              >
                <Select.Trigger className="w-full bg-gray-100 rounded-[10px] px-3.5 py-[13px] flex items-center justify-between text-[#828282] text-[12px]">
                  <Select.Value placeholder="Select" />
                  <Select.Icon>
                    <TbChevronDown
                      size={16}
                      className="text-[#828282] rotate-[270deg]"
                    />
                  </Select.Icon>
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content className="z-[200000] bg-white rounded-xl shadow-[0px_0px_24px_0px_rgba(0,0,0,0.08)] border-0 max-h-60 overflow-auto p-1">
                    <Select.Viewport>
                      {statuses.map((status) => (
                        <Select.Item
                          key={status.id}
                          value={status.id}
                          className="relative cursor-pointer select-none py-2.5 px-3 mx-1 rounded-lg hover:bg-gray-50 focus:bg-gray-50 data-[state=checked]:bg-blue-50 data-[state=checked]:text-blue-700 text-[#444444] text-[12px] font-medium transition-colors outline-none"
                        >
                          <Select.ItemText>{status.label}</Select.ItemText>
                        </Select.Item>
                      ))}
                    </Select.Viewport>
                  </Select.Content>
                </Select.Portal>
              </Select.Root>
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-gray-200 w-full"></div>

          {/* Default Assignment */}
          <div className="space-y-2">
            <h3 className="text-[#828282] text-[12px] font-bold uppercase">
              Default Assignment
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label.Root className="text-[#444444] text-[14px] font-semibold">
                  Assignee Type
                </Label.Root>
                <Select.Root
                  value={newTemplateData.default_assignee_type || "none"}
                  onValueChange={(value: "User" | "Team" | "none") => {
                    setNewTemplateData({
                      default_assignee_type:
                        value === "none" ? undefined : value,
                      default_assignee: undefined,
                    });
                    // Clear search term when assignee type changes
                    setAssigneeSearchTerm("");
                  }}
                >
                  <Select.Trigger className="w-full bg-gray-100 rounded-[10px] px-3.5 py-[13px] flex items-center justify-between text-[#828282] text-[12px]">
                    <Select.Value placeholder="Select" />
                    <Select.Icon>
                      <TbChevronDown
                        size={16}
                        className="text-[#828282] rotate-[270deg]"
                      />
                    </Select.Icon>
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Content className="z-[200000] bg-white rounded-xl shadow-[0px_0px_24px_0px_rgba(0,0,0,0.08)] border-0 max-h-60 overflow-auto p-1">
                      <Select.Viewport>
                        <Select.Item
                          value="none"
                          className="relative cursor-pointer select-none py-2.5 px-3 mx-1 rounded-lg hover:bg-gray-50 focus:bg-gray-50 data-[state=checked]:bg-blue-50 data-[state=checked]:text-blue-700 text-[#444444] text-[12px] font-medium transition-colors outline-none"
                        >
                          <Select.ItemText>
                            No Default Assignment
                          </Select.ItemText>
                        </Select.Item>
                        <Select.Item
                          value="User"
                          className="relative cursor-pointer select-none py-2.5 px-3 mx-1 rounded-lg hover:bg-gray-50 focus:bg-gray-50 data-[state=checked]:bg-blue-50 data-[state=checked]:text-blue-700 text-[#444444] text-[12px] font-medium transition-colors outline-none"
                        >
                          <Select.ItemText>User</Select.ItemText>
                        </Select.Item>
                        <Select.Item
                          value="Team"
                          className="relative cursor-pointer select-none py-2.5 px-3 mx-1 rounded-lg hover:bg-gray-50 focus:bg-gray-50 data-[state=checked]:bg-blue-50 data-[state=checked]:text-blue-700 text-[#444444] text-[12px] font-medium transition-colors outline-none"
                        >
                          <Select.ItemText>Team</Select.ItemText>
                        </Select.Item>
                      </Select.Viewport>
                    </Select.Content>
                  </Select.Portal>
                </Select.Root>
              </div>

              <div className="space-y-1.5">
                <Label.Root className="text-[#444444] text-[14px] font-semibold">
                  Default Assignee
                </Label.Root>
                <Select.Root
                  value={newTemplateData.default_assignee || "none"}
                  onValueChange={(value: string) =>
                    setNewTemplateData({
                      default_assignee: value === "none" ? undefined : value,
                    })
                  }
                  disabled={!newTemplateData.default_assignee_type}
                >
                  <Select.Trigger className="w-full bg-gray-100 rounded-[10px] px-3.5 py-[13px] flex items-center justify-between text-[#828282] text-[12px] disabled:opacity-50">
                    <Select.Value
                      placeholder={
                        newTemplateData.default_assignee_type
                          ? "Select"
                          : "Select assignee type first"
                      }
                    />
                    <Select.Icon>
                      <TbChevronDown
                        size={16}
                        className="text-[#828282] rotate-[270deg]"
                      />
                    </Select.Icon>
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Content className="z-[200000] bg-white rounded-xl shadow-[0px_0px_24px_0px_rgba(0,0,0,0.08)] border-0 max-h-60 overflow-auto p-1">
                      <Select.Viewport>
                        {/* Search Input */}
                        {newTemplateData.default_assignee_type && (
                          <div className="sticky top-0 bg-white p-2 border-b border-gray-100">
                            <div className="relative">
                              <TbSearch
                                size={14}
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                              />
                              <input
                                type="text"
                                placeholder={`Search ${newTemplateData.default_assignee_type.toLowerCase()}s...`}
                                value={assigneeSearchTerm}
                                onChange={(e) =>
                                  setAssigneeSearchTerm(e.target.value)
                                }
                                className="w-full pl-8 pr-3 py-2 text-[12px] bg-gray-50 rounded-lg border-0 outline-none focus:bg-white focus:ring-1 focus:ring-blue-200"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                        )}

                        <Select.Item
                          value="none"
                          className="relative cursor-pointer select-none py-2.5 px-3 mx-1 rounded-lg hover:bg-gray-50 focus:bg-gray-50 data-[state=checked]:bg-blue-50 data-[state=checked]:text-blue-700 text-[#444444] text-[12px] font-medium transition-colors outline-none"
                        >
                          <Select.ItemText>No Default Assignee</Select.ItemText>
                        </Select.Item>

                        {newTemplateData.default_assignee_type === "User" &&
                          filterUsers(assigneeSearchTerm).map((user) => (
                            <Select.Item
                              key={user.id}
                              value={user.id}
                              className="relative cursor-pointer select-none py-2.5 px-3 mx-1 rounded-lg hover:bg-gray-50 focus:bg-gray-50 data-[state=checked]:bg-blue-50 data-[state=checked]:text-blue-700 text-[#444444] text-[12px] font-medium transition-colors outline-none"
                            >
                              <Select.ItemText>{user.email}</Select.ItemText>
                            </Select.Item>
                          ))}

                        {newTemplateData.default_assignee_type === "Team" &&
                          filterTeams(assigneeSearchTerm).map((team) => (
                            <Select.Item
                              key={team.id}
                              value={team.id}
                              className="relative cursor-pointer select-none py-2.5 px-3 mx-1 rounded-lg hover:bg-gray-50 focus:bg-gray-50 data-[state=checked]:bg-blue-50 data-[state=checked]:text-blue-700 text-[#444444] text-[12px] font-medium transition-colors outline-none"
                            >
                              <Select.ItemText>{team.name}</Select.ItemText>
                            </Select.Item>
                          ))}

                        {/* No Results Message */}
                        {newTemplateData.default_assignee_type === "User" &&
                          assigneeSearchTerm &&
                          filterUsers(assigneeSearchTerm).length === 0 && (
                            <div className="py-2.5 px-3 mx-1 text-[#828282] text-[12px] text-center">
                              No users found
                            </div>
                          )}

                        {newTemplateData.default_assignee_type === "Team" &&
                          assigneeSearchTerm &&
                          filterTeams(assigneeSearchTerm).length === 0 && (
                            <div className="py-2.5 px-3 mx-1 text-[#828282] text-[12px] text-center">
                              No teams found
                            </div>
                          )}
                      </Select.Viewport>
                    </Select.Content>
                  </Select.Portal>
                </Select.Root>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-gray-200 w-full"></div>

          {/* Template Setting */}
          <div className="space-y-2">
            <h3 className="text-[#828282] text-[12px] font-bold uppercase">
              Template Setting
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label.Root className="text-[#444444] text-[14px] font-semibold">
                  Default Start Days
                </Label.Root>
                <div className="relative bg-gray-100 rounded-[10px] px-3.5 py-[13px] flex items-center justify-between">
                  <input
                    type="number"
                    value={newTemplateData.default_start_days || ""}
                    onChange={(e) => {
                      const value = e.target.value
                        ? parseInt(e.target.value)
                        : undefined;
                      setNewTemplateData({ default_start_days: value });
                      validateDays(value, newTemplateData.default_due_days);
                    }}
                    className="w-full bg-transparent text-[#828282] text-[12px] placeholder:text-[#828282] border-none outline-none"
                    placeholder="Days from creation"
                    min="0"
                  />
                  <TbCalendar size={16} className="text-[#828282] ml-2" />
                </div>
                {errors.start && (
                  <div className="text-red-500 text-xs">{errors.start}</div>
                )}
              </div>

              <div className="space-y-1.5">
                <Label.Root className="text-[#444444] text-[14px] font-semibold">
                  Default Due Days
                </Label.Root>
                <div className="relative bg-gray-100 rounded-[10px] px-3.5 py-[13px] flex items-center justify-between">
                  <input
                    type="number"
                    value={newTemplateData.default_due_days || ""}
                    onChange={(e) => {
                      const value = e.target.value
                        ? parseInt(e.target.value)
                        : undefined;
                      setNewTemplateData({ default_due_days: value });
                      validateDays(newTemplateData.default_start_days, value);
                    }}
                    className="w-full bg-transparent text-[#828282] text-[12px] placeholder:text-[#828282] border-none outline-none"
                    placeholder="Days from creation"
                    min="0"
                  />
                  <TbCalendar size={16} className="text-[#828282] ml-2" />
                </div>
                {errors.due && (
                  <div className="text-red-500 text-xs">{errors.due}</div>
                )}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-gray-200 w-full"></div>

          {/* Template Options */}
          <div className="space-y-3">
            <h3 className="text-[#828282] text-[12px] font-bold uppercase">
              Template Options
            </h3>
            <div className="grid grid-cols-3 gap-2">
              <label className="flex items-center gap-2">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={newTemplateData.auto_assign_steps || false}
                    onChange={(e) =>
                      setNewTemplateData({
                        auto_assign_steps: e.target.checked,
                      })
                    }
                    className="sr-only"
                  />
                  <div
                    className={`w-[18px] h-[18px] rounded-[5px] flex items-center justify-center ${
                      newTemplateData.auto_assign_steps
                        ? "bg-[#374795]"
                        : "border-[1.5px] border-[#bfbfbf]"
                    }`}
                  >
                    {newTemplateData.auto_assign_steps && (
                      <TbCheck size={14} className="text-white" />
                    )}
                  </div>
                </div>
                <span className="text-[#444444] text-[12px] font-semibold">
                  Auto-assign steps
                </span>
              </label>

              <label className="flex items-center gap-2">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={newTemplateData.require_photos || false}
                    onChange={(e) =>
                      setNewTemplateData({ require_photos: e.target.checked })
                    }
                    className="sr-only"
                  />
                  <div
                    className={`w-[18px] h-[18px] rounded-[5px] flex items-center justify-center ${
                      newTemplateData.require_photos
                        ? "bg-[#374795]"
                        : "border-[1.5px] border-[#bfbfbf]"
                    }`}
                  >
                    {newTemplateData.require_photos && (
                      <TbCheck size={14} className="text-white" />
                    )}
                  </div>
                </div>
                <span className="text-[#444444] text-[12px] font-semibold">
                  Require Photos
                </span>
              </label>

              <label className="flex items-center gap-2">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={newTemplateData.require_geometry || false}
                    onChange={(e) =>
                      setNewTemplateData({ require_geometry: e.target.checked })
                    }
                    className="sr-only"
                  />
                  <div
                    className={`w-[18px] h-[18px] rounded-[5px] flex items-center justify-center ${
                      newTemplateData.require_geometry
                        ? "bg-[#374795]"
                        : "border-[1.5px] border-[#bfbfbf]"
                    }`}
                  >
                    {newTemplateData.require_geometry && (
                      <TbCheck size={14} className="text-white" />
                    )}
                  </div>
                </div>
                <span className="text-[#444444] text-[12px] font-semibold">
                  Require Geometry
                </span>
              </label>
            </div>

            {newTemplateData.require_geometry && (
              <div className="mt-3">
                <Label.Root className="block text-sm font-medium text-gray-700 mb-2">
                  Allowed Geometry Types
                </Label.Root>
                <div className="flex flex-wrap gap-3">
                  {(["Point", "LineString", "Polygon"] as const).map((type) => (
                    <label key={type} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={
                          newTemplateData.allowed_geometry_types?.includes(
                            type
                          ) || false
                        }
                        onChange={(e) => {
                          const currentTypes =
                            newTemplateData.allowed_geometry_types || [];
                          const updatedTypes = e.target.checked
                            ? [...currentTypes, type]
                            : currentTypes.filter((t) => t !== type);

                          setNewTemplateData({
                            allowed_geometry_types:
                              updatedTypes.length > 0
                                ? updatedTypes
                                : undefined,
                          });
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-gray-700">{type}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Template Steps */}
          <div className="bg-gray-100 border border-[#374795] rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-[#444444] text-[14px] font-semibold">
                  Template Steps
                </span>
                <span className="text-[#828282] text-[12px] font-semibold">
                  ({templateSteps.length} Step)
                </span>
              </div>
              <button
                onClick={() => openTemplateSteps()}
                className="flex items-center gap-1 px-3 py-2 border border-[#374795] rounded-[10px]"
              >
                <TbPlus size={16} className="text-[#374795]" />
                <span className="text-[#374795] text-[12px] font-semibold">
                  Add Step
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Create Button */}
      <button
        onClick={async () => {
          if (
            validateDays(
              newTemplateData.default_start_days,
              newTemplateData.default_due_days
            )
          ) {
            setIsSaving(true);
            try {
              await onSubmit();
            } finally {
              setIsSaving(false);
            }
          }
        }}
        disabled={isSaving}
        className="flex justify-center self-stretch w-full mt-1 px-6 py-3 rounded-[10px] items-center gap-2.5 text-white bg-[#374795] hover:bg-[#132477] cursor-pointer text-sm font-semibold font-['Open_Sans'] leading-tight"
      >
        {isSaving ? (
          <span className="flex items-center gap-2">
            <TbLoader2 className="animate-spin" size={18} />
            <span className="text-white text-[14px] font-semibold">
              Saving...
            </span>
          </span>
        ) : (
          <span className="text-white text-[14px] font-semibold">
            {isEditing ? "Update" : "Create"} Template
          </span>
        )}
      </button>
    </div>
  );
};
